import React, { useState } from 'react'
import dynamic from 'next/dynamic'
import { useIntl } from 'react-intl'
import FormatDateRange from 'client/components/FormatDateRange'

import ButtonLink from 'src/comps/Atom/Buttons/ButtonLink'
import cn from 'classnames'
import styles from './styles.module.css'
import { Text } from 'src/comps/Atom/Text'
import { SIZE_SMALL } from '../Score/constants'
import Score from '../Score'
import { ManagementResponse } from './ManagementResponse'
import { useReviews } from 'src/hooks/useReviews'
import Button from 'src/comps/Atom/Buttons/Button'

const Expedia = dynamic(() => import('client/icons/comments/expedia'))
const Hotels = dynamic(() => import('client/icons/comments/hotels'))
const Vrbo = dynamic(() => import('client/icons/comments/vrbo'))
const Tripadvisor = dynamic(() => import('client/icons/Tripadvisor'))

const MAX_COMMENT_LENGTH = 280

export const logosConfig = {
  'expedia group': <Expedia height={16} width={92} />,
  'hotels.com': <Hotels height={16} width={92} />,
  vrbo: <Vrbo height={16} width={92} />,
  tripadvisor: <Tripadvisor height={16} width={24} />,
} as const

interface IRatingCommentsProps {
  paginate?: boolean
  loadMore?: () => void
}

const RatingComments = ({ loadMore, paginate = false }: IRatingCommentsProps) => {
  const { reviews, reviewsPaginated, isLoading, pagination } = useReviews()
  const { formatMessage } = useIntl()

  const itemsToShow = paginate ? reviewsPaginated : reviews
  const [isExpanded, setIsExpanded] = useState<Record<number, boolean>>({})

  const handleToggleExpand = (commentIndex: number) => {
    setIsExpanded(prev => ({
      ...prev,
      [commentIndex]: !isExpanded[commentIndex],
    }))
  }

  if (!itemsToShow || Object.keys(itemsToShow).length === 0) {
    return null
  }

  return (
    <div className={cn(styles.commentSection)}>
      {itemsToShow.map((comment, index) => {
        const ProviderLogo =
          logosConfig[
            comment.verificationSource.toLowerCase() as Lowercase<keyof typeof logosConfig>
          ] ?? null

        const totalLengthText =
          comment.text.length +
          comment.managementResponses.reduce((prev, res) => prev + res.text.length, 0)

        const showReadMore = totalLengthText > MAX_COMMENT_LENGTH
        const textToShow =
          comment.text.length > MAX_COMMENT_LENGTH && !isExpanded[index]
            ? `${comment.text.substring(0, MAX_COMMENT_LENGTH)}...`
            : comment.text

        return (
          <div
            className={styles.comment}
            key={comment.title + comment.author + comment.publishedDate}
            data-testid="comment"
          >
            <div className={styles.commentHeader}>
              <div className={styles.score}>
                <Score
                  value={Math.round(comment.stars * 2)}
                  size={SIZE_SMALL}
                  asymetricRoundedShape
                  toFixed={0}
                  neutral
                />
              </div>

              <Text as="p" variant="captionL" weight="weight3" className={styles.author}>
                {comment.author}
              </Text>
              <Text as="p" variant="captionL">
                <FormatDateRange date={comment.publishedDate} />
              </Text>
              <Text as="h3" variant="textBodyL" weight="weight3" className={styles.title}>
                {comment.title}
              </Text>
              <div className={styles.commentResume}>
                <span>{formatMessage({ id: 'global.via' })}</span>
                {ProviderLogo}
              </div>
            </div>
            <div className={cn(styles.commentContent)}>
              {comment.readMore ? (
                <>{comment.text}</>
              ) : (
                <Text as="p" variant="captionL">
                  {textToShow}
                </Text>
              )}
            </div>
            {comment.managementResponses.map(response => {
              const responseToShow =
                textToShow.length + response.text.length > MAX_COMMENT_LENGTH && !isExpanded[index]
                  ? `${response.text.substring(0, MAX_COMMENT_LENGTH - textToShow.length)}...`
                  : response.text
              return (
                responseToShow.length > 3 && (
                  <ManagementResponse
                    key={response.text}
                    date={response.date}
                    text={responseToShow}
                  />
                )
              )
            })}

            {showReadMore && (
              <ButtonLink
                className={styles.readMore}
                onClick={() => handleToggleExpand(index)}
                text={isExpanded[index] ? 'global.view-less' : 'global.read-full-review'}
              />
            )}
          </div>
        )
      })}

      {paginate && pagination.next && (
        <div className={styles.pagination}>
          <Button
            size="m"
            rounded
            backgroundColor="grey8"
            borderColor="grey4"
            color="grey1"
            isLoading={isLoading}
            onClick={loadMore}
          >
            {formatMessage({ id: 'global.read-more' })}
          </Button>
        </div>
      )}
    </div>
  )
}

export default RatingComments
